import React, { useState, useEffect, useRef } from 'react'
import { CircleCheck } from 'lucide-react'
import { useGetRequestTemplateQuery } from '../../../config/api/requestTemplate/requestTemplate.js'
import {
  useMatchFilesToDocumentsMutation,
  useMoveFileToDocumentMutation,
  useRemoveFileMutation,
} from '../../../config/api/requestTask/requestTask.js'
import * as requestTaskActions from '../requestTaskActions.js'
import * as taskActivityActions from '../../taskActivity/taskActivityActions.js'

// Import custom hooks
import { useFileHandling } from '../hooks/useFileHandling.js'
import { useDocumentManagement } from '../hooks/useDocumentManagement.js'
import { useFileUpload } from '../hooks/useFileUpload.js'

// Import components
import DocumentsHeader from './DocumentsHeader.jsx'
import DocumentsList from './DocumentsList.jsx'
import FileUploadArea from './FileUploadArea.jsx'

import NoDocumentsTask from './NoDocumentsTask.jsx'
import { useToast } from '../../../global/hooks/useToast.jsx'

export default function SingleRequestTaskDocuments({
  requestTaskId,
  requestTemplateId,
  selectedFirm,
  dispatch,
  userEmail,
  fromShareLink,
  loggedInUser,
}) {
  const firmId = selectedFirm._id
  const showToast = useToast()

  // API hooks
  const { data: requestTemplateData, isLoading: isRequestTemplateLoading } =
    useGetRequestTemplateQuery(
      {
        firmId: parseInt(firmId),
        requestTemplateId: String(requestTemplateId),
      },
      { skip: !firmId || !requestTemplateId },
    )

  const requestTemplate = requestTemplateData?.data

  const {
    requestTask,
    documents,
    unmatchedFiles,
    dragOverDocId,
    setDragOverDocId,
    isLoading: isRequestTaskLoading,
    isError: isRequestTaskError,
    refetch: refetchRequestTask,
  } = useDocumentManagement(requestTemplate, requestTaskId)

  const [showCompletionScreen, setShowCompletionScreen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const savingTimeoutRef = useRef(null)

  const [matchFilesToDocuments, { isLoading: isMatchingFiles }] =
    useMatchFilesToDocumentsMutation()
  const [moveFileToDocument, { isLoading: isMovingFile }] =
    useMoveFileToDocumentMutation()
  const [removeFileMutation] = useRemoveFileMutation()

  const {
    files,
    dragOver,
    setDragOver,
    handleFileUpload,
    handleDrop,
    removeFile,
    clearFiles,
    getValidFiles,
  } = useFileHandling()

  const { submitting, uploadItems, submitFiles, clearUploadItems } =
    useFileUpload()

  const isAnyOperationActive = isMatchingFiles || submitting || isMovingFile

  useEffect(() => {
    if (isAnyOperationActive) {
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current)
      }
      setIsSaving(true)
    } else {
      savingTimeoutRef.current = setTimeout(() => {
        setIsSaving(false)
      }, 2500)
    }

    return () => {
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current)
      }
    }
  }, [isAnyOperationActive])

  const handleDragFiles = async (fileId, fromDocId, toDocId) => {
    const fromDocIdNum =
      fromDocId === 'unmatched' ? 'unmatched' : Number(fromDocId)
    const toDocIdNum = toDocId === 'unmatched' ? 'unmatched' : Number(toDocId)

    if (fromDocIdNum === toDocIdNum) {
      return
    }

    try {
      await moveFileToDocument({
        requestTaskId: String(requestTask._id),
        fileId: parseInt(fileId),
        fromDocumentId: fromDocIdNum,
        toDocumentId: toDocIdNum === 'unmatched' ? null : toDocIdNum,
        firmId: selectedFirm._id,
      }).unwrap()
      showToast('File moved successfully', 'success')
    } catch (error) {
      console.error('Failed to move file:', error)
      showToast('Failed to move file. Please try again.', 'error')
    }
  }

  const handleDeleteFile = async (fileId, docId) => {
    try {
      await removeFileMutation({
        firmId: selectedFirm._id,
        requestTaskId: String(requestTask._id),
        documentId: docId === 'unmatched' ? undefined : docId,
        fileId: parseInt(fileId),
      }).unwrap()
      showToast('File deleted successfully', 'success')
    } catch (err) {
      showToast('Failed to delete file. Please try again.', 'error')
    }
  }

  const handleSubmitFiles = async e => {
    e.preventDefault()

    if (
      !requestTask ||
      (requestTask.status !== 'published' && requestTask.status !== 'rejected')
    ) {
      return
    }

    const validFiles = getValidFiles()

    if (validFiles.length < 1) {
      showToast('No files present', 'error')
      return
    }

    const requestParams = {
      _client: requestTask._client,
      _firm: selectedFirm._id,
      status: 'pending',
      type: 'request-task',
      hex: requestTask.hex,
      _folder: requestTask._folder,
    }

    if (
      userEmail &&
      fromShareLink &&
      !(loggedInUser && loggedInUser.username) &&
      requestTask?.assignee?.length
    ) {
      const user = requestTask.assignee.find(
        a => a.username?.toLowerCase() === userEmail.toLowerCase(),
      )
      if (user?.firstname && user?.lastname) {
        requestParams.uploadName = `${user.firstname} ${user.lastname}`
      } else if (user?.username) {
        requestParams.uploadName = user.username
      } else {
        requestParams.uploadName = `(not verified user)`
      }
    }

    try {
      const { createdFiles } = await submitFiles({
        files: validFiles,
        dispatch,
        requestParams,
      })

      if (createdFiles.length) {
        try {
          await matchFilesToDocuments({
            requestTaskId: String(requestTask._id),
            files: createdFiles.map(f => f._id),
            firmId: selectedFirm._id,
          }).unwrap()

          showToast('All files uploaded and matched successfully', 'success')
        } catch (err) {
          console.error('Failed to match files to documents', err)
          showToast('Failed to match files to documents', 'error')
        }
      }
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error')
    }

    clearFiles()
    clearUploadItems()
  }

  const handleFinishTask = async () => {
    if (!requestTask || !selectedFirm) return

    try {
      const documentFileIds = documents
        .flatMap(doc => doc.files || [])
        .map(file => file._id)
      const unmatchedFileIds = unmatchedFiles.map(file => file._id)
      const allFileIds = [...documentFileIds, ...unmatchedFileIds].filter(
        id => id,
      )

      const updatedRequestTask = { ...requestTask }
      updatedRequestTask._firm = selectedFirm._id

      updatedRequestTask._returnedFiles = [
        ...(requestTask._returnedFiles || []),
        ...allFileIds.filter(id => !requestTask._returnedFiles?.includes(id)),
      ].filter((id, index, self) => self.indexOf(id) === index)

      if (
        userEmail &&
        fromShareLink &&
        !(loggedInUser && loggedInUser.username)
      ) {
        updatedRequestTask.user = { username: userEmail }
      }

      const response = await dispatch(
        requestTaskActions.sendUpdateRequestTaskbyClientUser(
          updatedRequestTask,
        ),
      )

      if (response.success) {
        refetchRequestTask()
        setShowCompletionScreen(true)
        if (response.taskActivity) {
          dispatch(
            taskActivityActions.addSingleTaskActivityToMap(
              response.taskActivity,
            ),
          )
          dispatch(
            taskActivityActions.addTaskActivityToList(
              response.taskActivity,
              ...['_requestTask', requestTask._id],
            ),
          )
        }
      } else {
        showToast('Failed to complete task. Please try again.', 'error')
      }
    } catch (error) {
      console.error('Error completing task:', error)
      showToast(
        'An error occurred while completing the task. Please try again.',
        'error',
      )
    }
  }

  if (!requestTask || !selectedFirm) {
    return null
  }

  if (isRequestTaskLoading || isRequestTemplateLoading) {
    return <div className="tw-p-8">Loading request task data...</div>
  }

  if (isRequestTaskError) {
    return (
      <div className="tw-p-8">
        Error loading request task data. Please try again.
      </div>
    )
  }

  if (
    showCompletionScreen ||
    requestTask?.status === 'completed' ||
    requestTask?.status === 'under_review'
  ) {
    return (
      <div className="tw-mt-10 tw-flex tw-flex-col tw-items-center tw-text-center">
        <CircleCheck
          aria-hidden="true"
          className="tw-mb-4 tw-size-24 tw-text-success"
        />
        <h3 className="tw-mb-2 tw-font-bold">
          All documents have been submitted!
        </h3>
        <h4 className="u-muted tw-m-0">
          Thank you for uploading all requested files. The requesting team has
          been notified.
        </h4>
      </div>
    )
  }

  if (!documents || documents.length === 0) {
    return (
      <NoDocumentsTask
        selectedFirm={selectedFirm}
        requestTask={requestTask}
        unmatchedFiles={unmatchedFiles}
        files={files}
        dragOver={dragOver}
        setDragOver={setDragOver}
        onFileUpload={handleFileUpload}
        onDrop={handleDrop}
        onRemoveFile={removeFile}
        submitting={submitting}
        uploadItems={uploadItems}
        onSubmitFiles={handleSubmitFiles}
        onFinishTask={handleFinishTask}
        onDeleteFile={handleDeleteFile}
        isMatchingFiles={isMatchingFiles}
        isSaving={isSaving}
      />
    )
  }

  return (
    <div className="tw-mx-auto tw-max-w-[900px] tw-p-8">
      <DocumentsHeader
        selectedFirm={selectedFirm}
        requestTask={requestTask}
        documents={documents}
        onFinishTask={handleFinishTask}
        unmatchedFiles={unmatchedFiles}
        isSaving={isSaving}
      />

      <DocumentsList
        requestTask={requestTask}
        documents={documents}
        dragOverDocId={dragOverDocId}
        setDragOverDocId={setDragOverDocId}
        onDragFiles={handleDragFiles}
        onDeleteFile={handleDeleteFile}
        unmatchedFiles={unmatchedFiles}
      />

      <FileUploadArea
        files={files}
        dragOver={dragOver}
        setDragOver={setDragOver}
        onFileUpload={handleFileUpload}
        onDrop={handleDrop}
        onRemoveFile={removeFile}
        submitting={submitting}
        uploadItems={uploadItems}
        onSubmitFiles={handleSubmitFiles}
        hasDocuments={true}
        isMatchingFiles={isMatchingFiles}
      />
    </div>
  )
}
